/**
 * The stylesheet of the API's server-rendered sign-in pages (the invitation link, #1933): the approved two-column
 * layout the portal draws with ZhAuthLayout, as plain CSS. Vue-free, and a string, because the page may load
 * nothing: the API inlines it.
 *
 * Built from the theme tokens (tokens.ts), so it cannot drift from the portal again: the page it replaces had
 * hand-copied hex and a magenta call to action. The one accent, electric cyan, fills only the primary button; control
 * borders and dividers are dirty white mixed toward the ground, as the theme's ink and line steps are; every corner is
 * square. Only the tokens the page uses are declared, and magenta is not one of them.
 *
 * The faces are named, not fetched: a visitor with Space Grotesk, Anton or IBM Plex Mono installed sees them, and
 * anyone else the stack's fallbacks, as the page's Content-Security-Policy loads no font.
 */
import { FONT_FAMILY, PALETTE, SURFACE } from '../tokens'

/** The theme tokens the page reads, under the names theme.css gives them. */
const TOKENS: Record<string, string> = {
    '--void-black': PALETTE.voidBlack,
    '--system-charcoal': PALETTE.systemCharcoal,
    '--dirty-white': PALETTE.dirtyWhite,
    '--electric-cyan': PALETTE.electricCyan,
    '--success-green': PALETTE.successGreen,
    '--warning-amber': PALETTE.warningAmber,
    '--state-complete': 'var(--success-green)',
    '--state-active': 'var(--warning-amber)',
    '--state-action': 'var(--electric-cyan)',
    '--font-display': FONT_FAMILY.display,
    '--font-body': FONT_FAMILY.body,
    '--font-mono': FONT_FAMILY.mono,
}

/** Secondary text: the theme's second ink step. */
const MUTED = SURFACE.ink2
/** A control's edge: the theme's line step. */
const LINE = SURFACE.line
/** A divider or a panel's edge: the theme's hairline step. */
const HAIR = SURFACE.hair
/** The system voice (BG §19.3): mono, uppercase, the theme's system tracking (theme.css --tracking-system). */
const SYSTEM_VOICE = 'font-family:var(--font-mono);letter-spacing:0.06em;text-transform:uppercase'
/** The display voice (ZhPageTitle): Anton, uppercase, the theme's display tracking (theme.css --tracking-display). */
const DISPLAY_VOICE =
    'font-family:var(--font-display);font-weight:400;letter-spacing:0.02em;text-transform:uppercase;overflow-wrap:anywhere'

const RULES = [
    `:root{${Object.entries(TOKENS)
        .map(([name, value]) => `${name}:${value}`)
        .join(';')}}`,
    '*{box-sizing:border-box}',
    'body{margin:0;min-height:100vh;background:var(--void-black);color:var(--dirty-white);font-family:var(--font-body);line-height:1.5}',
    'p{margin:0}',
    '.auth{display:flex;min-height:100vh}',
    '.auth-main{display:flex;flex-direction:column;flex:1 1 auto;min-width:0;gap:32px;padding:24px}',
    '.logo{display:flex;align-items:center;gap:12px}',
    '.logo-mark{display:block;width:24px;height:24px}',
    '.logo-mark-ground{fill:var(--void-black)}',
    '.logo-mark-glyph{fill:var(--dirty-white)}',
    '.logo-mark-band{fill:var(--electric-cyan)}',
    `.label{${SYSTEM_VOICE};font-size:13px;font-weight:500}`,
    '.auth-form{display:flex;flex-direction:column;justify-content:center;flex:1 1 auto;gap:24px;width:100%;max-width:400px;margin:0 auto}',
    '.heading{display:flex;flex-direction:column;gap:8px}',
    `.tile{display:flex;align-items:center;justify-content:center;width:48px;height:48px;margin-bottom:8px;background:var(--system-charcoal);border:1px solid ${HAIR};${DISPLAY_VOICE};font-size:22px}`,
    `h1{margin:0;${DISPLAY_VOICE};font-size:30px;line-height:1.05}`,
    `.muted{color:${MUTED}}`,
    '.muted strong{color:var(--dirty-white);font-weight:500}',
    `.notice{padding:12px;border:1px solid ${LINE}}`,
    '.providers{display:flex;flex-direction:column;gap:12px}',
    'form{margin:0}',
    `button{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;height:48px;padding:0 28px;border-radius:0;${SYSTEM_VOICE};font-size:13px;font-weight:700;cursor:pointer}`,
    `.outline{background:transparent;color:var(--dirty-white);border:1px solid ${LINE}}`,
    '.primary{background:var(--electric-cyan);color:var(--void-black);border:0}',
    'button:focus-visible,input:focus-visible,a:focus-visible{outline:2px solid var(--electric-cyan);outline-offset:2px}',
    `.divider{display:flex;align-items:center;gap:12px;${SYSTEM_VOICE};font-size:12px;color:${MUTED}}`,
    `.divider::before,.divider::after{content:"";flex:1 1 auto;border-top:1px solid ${HAIR}}`,
    '.email{display:flex;flex-direction:column;gap:12px}',
    `label{${SYSTEM_VOICE};font-size:12px;font-weight:500}`,
    `input{width:100%;height:48px;padding:0 12px;border:1px solid ${LINE};border-radius:0;background:var(--void-black);color:var(--dirty-white);font:inherit}`,
    `input[readonly]{color:${MUTED}}`,
    `.helper,.small{font-size:13px;color:${MUTED}}`,
    `.footer{font-size:13px;color:${MUTED}}`,
    '.footer a{color:var(--dirty-white);text-decoration:underline;text-underline-offset:4px}',
    '.splash{display:flex;flex:0 0 45%;padding:24px 24px 24px 0}',
    `.splash-panel{display:flex;flex-direction:column;justify-content:center;gap:40px;width:100%;padding:64px 56px;background:var(--system-charcoal);border:1px solid ${HAIR}}`,
    `.splash h2{margin:0;${DISPLAY_VOICE};font-size:44px;line-height:1.1}`,
    '.example{display:flex;flex-direction:column;gap:16px}',
    `.example-card{display:flex;flex-direction:column;gap:12px;padding:20px;background:${SURFACE.panel2};border:1px solid ${HAIR}}`,
    '.example-head{display:flex;align-items:center;gap:12px}',
    `.avatar{display:flex;align-items:center;justify-content:center;width:32px;height:32px;border:1px solid ${HAIR};${SYSTEM_VOICE};font-size:11px}`,
    '.who{font-size:14px;font-weight:500}',
    `.pill{display:inline-flex;align-items:center;gap:6px;margin-left:auto;padding:2px 7px;${SYSTEM_VOICE};font-size:12px;color:var(--state-action);background:color-mix(in srgb, var(--state-action) 10%, transparent)}`,
    '.example-actions{display:flex;gap:8px}',
    `.chip{display:inline-flex;align-items:center;height:28px;padding:0 10px;${SYSTEM_VOICE};font-size:12px;font-weight:700}`,
    `.chip-outline{border:1px solid ${LINE}}`,
    `.activity{display:flex;align-items:center;gap:8px;margin-left:32px;font-size:14px;color:${MUTED}}`,
    '.activity + .activity{margin-left:64px}',
    '.dot{display:block;flex-shrink:0}',
    '.dot-complete{fill:var(--state-complete)}',
    '.dot-active{fill:var(--state-active)}',
    '@media (min-width:1024px){.auth-main{padding:40px 56px}.logo-mark{width:28px;height:28px}h1{font-size:34px}}',
    '@media (max-width:1023.98px){.splash{display:none}}',
]

export const AUTH_PAGE_CSS = RULES.join('\n')
